import { store } from '../store';
import { registerDevice } from '../store/notificationsSlice';
import { isIOS, isStandalone } from './deviceDetect';

let cachedDeviceId: string | null = null;
export const NOTIFICATION_PROMPT_STORAGE_KEY = 'notification_permission_prompt_v1';

export const getOrCreateDeviceId = (): string => {
  if (cachedDeviceId) return cachedDeviceId;

  if (typeof window !== 'undefined' && window.localStorage) {
    let devId = window.localStorage.getItem('pwa_device_id');
    if (!devId) {
      devId = 'pwa-' + Math.random().toString(36).substring(2, 15);
      window.localStorage.setItem('pwa_device_id', devId);
    }
    cachedDeviceId = devId;
    return devId;
  }

  cachedDeviceId = 'pwa-dev-' + Math.random().toString(36).substring(2, 15);
  return cachedDeviceId;
};

export const getAppInstanceId = (workspaceId: string): string => {
  if (workspaceId === 'workspace-2') return 'app-2';
  if (workspaceId.startsWith('workspace-')) return workspaceId.replace('workspace-', 'app-');
  return 'app-1';
};

export const isPushSupported = (): boolean => {
  if (typeof window === 'undefined') return false;
  return 'Notification' in window && 'serviceWorker' in navigator;
};

export type NotificationPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

export const getNotificationPermissionState = (): NotificationPermissionState => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission as NotificationPermissionState;
};

export const getNotificationPromptDismissed = (): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const raw = window.localStorage.getItem(NOTIFICATION_PROMPT_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed.status === 'dismissed' || parsed.status === 'granted';
  } catch (err) {
    return false;
  }
};

export const setNotificationPromptDismissed = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(
      NOTIFICATION_PROMPT_STORAGE_KEY,
      JSON.stringify({ status: 'dismissed', timestamp: Date.now() })
    );
  }
};

export const setNotificationPromptGranted = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(
      NOTIFICATION_PROMPT_STORAGE_KEY,
      JSON.stringify({ status: 'granted', timestamp: Date.now() })
    );
  }
};

/**
 * Silent sync of push notifications if permission is already granted.
 * Never requests permission directly on initial load.
 */
export const initializePushNotifications = async (workspaceId: string) => {
  try {
    if (!isPushSupported()) {
      return;
    }

    // On iOS Safari, push notifications only function when running as an installed standalone PWA
    if (isIOS() && !isStandalone()) {
      return;
    }

    if (Notification.permission === 'granted') {
      const appInstanceId = getAppInstanceId(workspaceId);
      const deviceId = getOrCreateDeviceId();
      const mockPushToken = 'web-push-token-' + deviceId;

      await store.dispatch(
        registerDevice({
          deviceId,
          fcmToken: mockPushToken,
          platform: 'WEB',
          appVersion: '1.0.0',
          appInstanceId,
        })
      ).unwrap();
    }
  } catch (err) {
    console.warn('⚠️ [PWA Push]: Error during background push subscription sync:', err);
  }
};

/**
 * User-initiated request to grant push notification permissions and register the device.
 */
export const requestPushPermission = async (workspaceId: string): Promise<{ success: boolean; state: NotificationPermissionState; message?: string }> => {
  if (!isPushSupported()) {
    return { success: false, state: 'unsupported', message: 'Web Notifications are not supported in this browser.' };
  }

  // iOS Safari requires standalone PWA installation for Web Push
  if (isIOS() && !isStandalone()) {
    return {
      success: false,
      state: 'default',
      message: 'Please install the app to your Home Screen to enable notifications on iOS.',
    };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setNotificationPromptGranted();
      const deviceId = getOrCreateDeviceId();
      const appInstanceId = getAppInstanceId(workspaceId);
      const mockPushToken = 'web-push-token-' + deviceId;

      await store.dispatch(
        registerDevice({
          deviceId,
          fcmToken: mockPushToken,
          platform: 'WEB',
          appVersion: '1.0.0',
          appInstanceId,
        })
      ).unwrap();

      return { success: true, state: 'granted' };
    }

    return {
      success: false,
      state: permission as NotificationPermissionState,
      message: permission === 'denied' ? 'Notification permissions were blocked in browser settings.' : undefined,
    };
  } catch (err: any) {
    console.warn('⚠️ [PWA Push]: Failed to request notification permission:', err);
    return {
      success: false,
      state: getNotificationPermissionState(),
      message: err.message || 'Notification registration failed.',
    };
  }
};
